import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import {
  Note,
  TripAcceptance,
  TripDetails,
  TripResponse,
  TripStop,
} from "../types/api";
import { tripsService } from "../api/tripsService/tripsServices";

interface TripContextType {
  trips: TripResponse[];
  loading: boolean;
  error: string | null;
  tripId: number | null;
  setTripId: (id: number) => void;

  setTrips: (trips: TripResponse[]) => void;
  tripDetails: TripDetails | null;
  setTripDetails: (details: TripDetails) => void;
  clearTripDetails: () => void;
  tripAcceptance: TripAcceptance | null;
  setTripAcceptance: (acceptance: TripAcceptance) => void;
  fetchTripData: (id: number) => Promise<any | null>;

  fetchTripAcceptance: (
    id: number,
    legacyCodes: { legacycode: string; company: string }[]
  ) => Promise<void>;
  licensePlates: { licensePlate: string; equipmentType: string, isUpdatable: boolean }[] ;
  fetchLicensePlates: (tripId: number) => Promise<void>;
  stops: TripStop[];
  fetchStops: (tripId: number) => Promise<void>;
  tripEndpoints: TripStop[];
  notes: Note[] | null;
  fetchNotes: (tripId: number) => Promise<void>;
  isDataStale: boolean;
  setIsDataStale: (isStale: boolean) => void;
  fetchLastUpdateData: (data: any) => Promise<void>;
  fetchAllPosition: (data: any) => Promise<void>;
  lastUpdateData: any;
  allPosition: any;

  tripsLoader: boolean;
  tripsError: string | null;
  tripsDetailsLoader: boolean;
  tripsDetailsError: string | null;
  tripIdLoader: boolean;
  tripIdError: string | null;
  tripAcceptanceLoader: boolean;
  tripAcceptanceError: string | null;
  licensePlateLoader: boolean;
  licensePlateError: string | null;
  stopsLoader: boolean;
  stopsError: string | null;
  notesLoader: boolean;
  notesError: string | null;

}

const TripContext = createContext<TripContextType | undefined>(undefined);

export const TripProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [trips, setTrips] = useState<TripResponse[]>([]);
  const [tripsLoader, setTripsLoader] = useState<boolean>(false);
  const [tripsError, setTripsError] = useState<string | null>(null);

  const [tripDetails, setTripDetailsState] = useState<TripDetails | null>(null);
  const [tripsDetailsLoader, setTripsDetailsLoader] = useState<boolean>(false);
  const [tripsDetailsError, setTripsDetailsError] = useState<string | null>(
    null
  );

  const [tripId, setTripId] = useState<number | null>(null);
  const [tripIdLoader, setTripIdLoader] = useState<boolean>(false);
  const [tripIdError, setTripIdError] = useState<string | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [tripAcceptance, setTripAcceptance] = useState<TripAcceptance | null>(null);
  const [tripAcceptanceLoader, setTripAcceptanceLoader] = useState<boolean>(false);
  const [tripAcceptanceError, setTripAcceptanceError] = useState<string | null>(null);

  const [licensePlates, setLicensePlates] = useState<
    { licensePlate: string; equipmentType: string, isUpdatable: boolean }[]
  >([{licensePlate:"-",equipmentType:"-", isUpdatable: true}]);
  const [licensePlateLoader, setLicensePlateLoader] = useState<boolean>(false);
  const [licensePlateError, setLicensePlateError] = useState<string | null>(null);


  const [stops, setStops] = useState<TripStop[]>([]);
  const [stopsLoader, setStopsLoader] = useState<boolean>(false);
  const [stopsError, setStopsError] = useState<string | null>(null);



  const [tripEndpoints, setTripEndpoints] = useState<TripStop[]>([]);
  const [tripEndpointsLoader, setTripEndopointsLoader] = useState<boolean>(false);
  const [tripEndpointsError, setTripEndpointsError] = useState<string | null>(null);


  const [notes, setNotes] = useState<Note[] | null>(null);
  const [notesLoader, setNotesLoader] = useState<boolean>(false);
  const [notesError, setNotesError] = useState<string | null>(null);


  const [isDataStale, setIsDataStale] = useState<boolean>(true);
  const [lastUpdateData, setLastUpdateData] = useState<any | null>(null);
  const [allPosition, setAllPosition] = useState<any | null>(null);

  const setTripDetails = (details: TripDetails | null) => {
    setTripDetailsState(details);
    setLoading(false);
  };

  const clearTripDetails = () => {
    setTripDetailsState(null);
    setTripAcceptance(null);
    setStops([]);
    setNotes(null);
    setLastUpdateData(null);
    setAllPosition(null);
    setLicensePlates([])
  };

  const fetchTripData = async (id: number) => {
  
    setTripsLoader(true);
    setTripsError(null);
    
    // PULISCI IMMEDIATAMENTE i dati precedenti
    setTripDetails(null);

    try {
      const tripDetailsResponse = await tripsService.getTripHeaderById(id);
      setTripDetails(tripDetailsResponse);
      return tripDetailsResponse
    } catch (err) {
      setTripsError("Failed to fetch trip data.");
    } finally {
      setTripsLoader(false);
    }
  };

  const fetchTripAcceptance = async (id: number, legacyCodes: any[]) => {
 
    setTripAcceptanceLoader(true);
    setTripAcceptanceError(null);
    
    // PULISCI IMMEDIATAMENTE i dati precedenti
    setTripAcceptance(null);

    try {
      const tripAcceptanceResponse = await tripsService.getTripAcceptanceById(
        id,
        legacyCodes
      );
      setTripAcceptance(tripAcceptanceResponse);
    } catch (err) {
      setTripAcceptanceError("Failed to fetch trip data.");
    } finally {
      setTripAcceptanceLoader(false);
    }
  };

  const fetchLicensePlates = async (tripId: number) => {
    setLicensePlateLoader(true);
    setLicensePlateError(null);
    
    // PULISCI IMMEDIATAMENTE i dati precedenti
    setLicensePlates([]);
    
    try {
      const plates = await tripsService.getLicensePlatesByTripId(tripId);
      setLicensePlates(plates);
    } catch (err) {
      setLicensePlateError("Failed to fetch license plates.");
    } finally {
      setLicensePlateLoader(false);
    }
  };

  const fetchStops = async (tripId: number) => {
    setStopsLoader(true);
    setStopsError(null);
    
    // PULISCI IMMEDIATAMENTE i dati precedenti
    setStops([]);
    
    try {
      const stopsResponse = await tripsService.getTripTimelineById(tripId);
      setStops(stopsResponse);
    } catch (err) {
      setStopsError("Failed to fetch stops.");
    } finally {
      setStopsLoader(false);
    }
  };

  const fetchNotes = async (tripId: number) => {
    setNotesLoader(true);
    setNotesError(null);
    
    // PULISCI IMMEDIATAMENTE i dati precedenti
    setNotes(null);
    
    try {
      const notesResponse = await tripsService.getTripNotesById(tripId);
      setNotes(notesResponse);
    } catch (err) {
      setNotesError("Failed to fetch notes.");
    } finally {
      setNotesLoader(false);
    }
  };

  const fetchLastUpdateData = useCallback(
    async (
      orderList: {
        erpSystem: string;
        erpCompany: string;
        orderType: number;
        orderNumber: string;
      }[]
    ): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const response = await tripsService.getLastOrdersPositions({
          orderList,
        });
        if (response.success) {
          setLastUpdateData(response);
        } else {
          setLastUpdateData([]);
        }
      } catch (error) {
        setLastUpdateData([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchAllPosition = useCallback(
    async (data: {
      erpSystem: string;
      erpCompany: string;
      orderType: number;
      orderNumber: string;
    }): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const response = await tripsService.getAllOrdersPositions(data);
        if (response.success) {
          setAllPosition(response);
        } else {
          setAllPosition([]);
        }
      } catch (error) {
        setAllPosition([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (stops.length > 0) {
      setTripEndpoints([stops[0], stops[stops.length - 1]]);
    } else {
      setTripEndpoints([]);
    }
  }, [stops]);

  return (
    <TripContext.Provider
      value={{
        trips,
        setTrips,
        loading,
        error,
        tripId,
        setTripId,
        tripDetails,
        setTripDetails,
        clearTripDetails,
        tripAcceptance,
        setTripAcceptance,
        fetchTripData,
        licensePlates,
        fetchLicensePlates,
        fetchStops,
        stops,
        tripEndpoints,
        notes,
        fetchNotes,
        isDataStale,
        setIsDataStale,
        fetchLastUpdateData,
        lastUpdateData,
        fetchAllPosition,
        allPosition,
        fetchTripAcceptance,
        tripsLoader,
        tripsError,
        tripsDetailsLoader,
        tripsDetailsError,
        tripIdLoader,
        tripIdError,
        tripAcceptanceLoader,
        tripAcceptanceError,
        licensePlateLoader,
        licensePlateError,
        stopsLoader,
        stopsError,
        notesLoader,
        notesError,
      }}
    >
      {children}
    </TripContext.Provider>
  );
};

export const useTripContext = (): TripContextType => {
  const context = useContext(TripContext);
  if (!context) {
    throw new Error("useTripContext must be used within a TripProvider");
  }
  return context;
};
